/**
 * AutismSupportingDocumentsPanel — the "Supporting Documents" panel shown on
 * Adult Autism MDT Review and Post-Assessment Discussion (PAD) pages.
 *
 * This is the Autism counterpart to the ADHD MDT/PAD supporting-docs panels
 * (8_diagnosticDecision/0_DDReview.js, DocumentPreviewPanel.js): the same
 * gray-50 rounded card, the same document cards (large icon + title + "View"
 * button that opens the rendered PDF in an embedded viewer). Display titles
 * use Autism stage names (History, not Clinical Interview).
 *
 * ─── HOW IT WORKS ────────────────────────────────────────────────────────────
 *
 * 1. LIVE DOCUMENTS: We attach a scoped onSnapshot listener to patients/{id} and
 *    read its `documents` map. We listen directly (rather than reading the
 *    modular context's `header`) because that context loads via a one-shot getDoc
 *    and can be restored stale from sessionStorage — so it would not reflect a
 *    report that was rendered moments earlier in the same workflow. The listener
 *    is read-only and unsubscribes on unmount / patient change.
 *
 * 2. WHICH CARDS SHOW: getAvailableSupportingDocuments() returns one entry per
 *    upstream report that has a rendered PDF (documents.{key}.pdfPath). Reports
 *    without a PDF (e.g. PDF conversion failed but the DOCX was stored, or the
 *    section was never rendered) are omitted, so we never open a broken viewer.
 *    If nothing is previewable the whole panel is hidden (parity with ADHD).
 *
 * 3. PREVIEW: Clicking "View" opens the shared DocumentEmbedDialog, reusing the
 *    same fetchLatestDocumentForEmbed pipeline as ADHD. `lastGenerated` is
 *    threaded through so the embed-URL cache invalidates correctly after a
 *    re-render. Each card prefetches its URL on mount/hover to make the first
 *    open feel instant (the dialog reads the same per-tab cache).
 *
 * Note: this panel is View-only (matching the ADHD PAD panel). Download lives on
 * the Documents Dashboard, not here.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { doc as firestoreDoc, onSnapshot } from 'firebase/firestore';
import { DocumentTextIcon, SearchIcon } from '@heroicons/react/solid';
import { db } from '../../firebase';
import { getPatientHeaderPathSegments } from '../../constants/adultAutismFirestorePaths';
import { getAvailableSupportingDocuments } from '../../utils/autismDocumentActions';
import { DocumentEmbedDialog } from '../../pages/providers/8_diagnosticDecision/components/DocumentEmbedDialog';
import { useEmbeddedReportUrl } from '../../hooks/useEmbeddedReportUrl';
import { formatDateTime } from '../../utils/timestampUtils';

/**
 * A single supporting-document card. Kept as its own component so the
 * useEmbeddedReportUrl hook (used for prefetching) is called once per card with a
 * stable identity — calling hooks inside a .map() in the parent would violate the
 * rules of hooks when the available list changes.
 */
function SupportingDocumentCard({ patientId, clinicianId, report, onView }) {
  const { prefetch } = useEmbeddedReportUrl(
    patientId,
    report.reportStage,
    report.lastGenerated,
    clinicianId,
  );

  // Warm the embed-URL cache on mount so the first "View" opens instantly.
  useEffect(() => {
    prefetch?.().catch(() => {});
  }, [prefetch]);

  return (
    <div className="flex flex-col items-center text-center">
      <div>
        <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-2" aria-hidden="true" />
        <h4 className="font-medium text-gray-800 mb-1">{report.title}</h4>
        {report.lastGenerated && (
          <p className="text-xs text-gray-500 mt-1">
            Last updated on {formatDateTime(report.lastGenerated)}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onView(report)}
        onMouseEnter={() => { try { prefetch?.(); } catch { /* best-effort */ } }}
        onFocus={() => { try { prefetch?.(); } catch { /* best-effort */ } }}
        className="mt-3 px-4 py-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-md transition-colors flex items-center gap-1"
      >
        <SearchIcon className="h-4 w-4" aria-hidden="true" />
        View
      </button>
    </div>
  );
}

export default function AutismSupportingDocumentsPanel({
  patientId,
  clinicianId,
  className = '',
}) {
  // Live `documents` map from the patient header (see header docstring for why a
  // dedicated listener is used rather than the modular context's header).
  const [documents, setDocuments] = useState(null);

  // Preview dialog state. We keep the selected report stage + its lastGenerated
  // so the embed dialog fetches the correct, cache-fresh URL.
  const [selected, setSelected] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    if (!patientId) return undefined;
    const headerRef = firestoreDoc(db, ...getPatientHeaderPathSegments(patientId));
    const unsub = onSnapshot(
      headerRef,
      (snap) => setDocuments(snap.exists() ? (snap.data().documents || null) : null),
      (err) => console.error('Autism supporting documents listener error:', err),
    );
    return () => unsub();
  }, [patientId]);

  const available = useMemo(
    () => getAvailableSupportingDocuments(documents),
    [documents],
  );

  const handleView = useCallback((report) => {
    // `report.title` is already the TOC-facing display name (History, MDT
    // Review, Feedback Session, …) derived in SUPPORTING_DOCUMENT_DISPLAY, so we
    // thread it into the dialog chrome while the embed fetch still uses the
    // internal reportStage.
    setSelected({
      reportStage: report.reportStage,
      lastGenerated: report.lastGenerated,
      displayTitle: report.title,
    });
    setIsPreviewOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsPreviewOpen(false);
    // Defer clearing the selection until the dialog close animation finishes so
    // the title/iframe don't blank out mid-transition.
    setTimeout(() => setSelected(null), 300);
  }, []);

  // Hide the panel entirely when there is nothing to preview (parity with the
  // ADHD PAD panel). Also guards against being rendered without a patientId.
  if (!patientId || available.length === 0) return null;

  return (
    <div className={`bg-gray-50 rounded-2xl border border-gray-200 p-6 ${className}`}>
      <h3 className="text-lg font-semibold mb-4">Supporting Documents</h3>
      <div className="flex flex-wrap gap-8">
        {available.map((report) => (
          <SupportingDocumentCard
            key={report.stageKey}
            patientId={patientId}
            clinicianId={clinicianId}
            report={report}
            onView={handleView}
          />
        ))}
      </div>

      <DocumentEmbedDialog
        isOpen={isPreviewOpen}
        onClose={handleClose}
        reportStage={selected?.reportStage}
        clinicianId={clinicianId}
        patientId={patientId}
        lastGenerated={selected?.lastGenerated}
        displayTitle={selected?.displayTitle}
      />
    </div>
  );
}
